import { Injectable, NgZone } from '@angular/core';
import { ApolloError, ServerError } from '@apollo/client';
import { AppView } from '@coach-bot/data-access/shared/enums';
import { LocalStorageItem } from '@coach-bot/data-access/shared/interfaces';
import { UserDetails } from '@coach-bot/data-access/shared/models';
import {
  AbstractApiRequestService,
  AbstractLocalStorageService,
} from '@coach-bot/data-access/shared/services/abstract';
import { AnalyticsEvent } from '@coach-bot/features/analytics';
import { captureSentryMessage } from '@shared/sentry';
import { ONE_SECOND_MS } from '@shared/utils/constants';
import { Dict } from 'mixpanel-browser';
import { Observable, Observer, Subject } from 'rxjs';
import { ApiRequestType, AppAction, BackgroundAction, ContentScriptAction, DataType } from '../enums';
import { ApiRequest, ApiResponse, GraphQLRequestData } from '../interfaces';
import { hasChromeRuntime, safeSendChromeRuntimeMessage, safeSendChromeRuntimeMessageAsync } from '../utils';

const CHECK_SERVICE_WORKER_STATE_TIMEOUT_MS = ONE_SECOND_MS * 5;

@Injectable()
export class ExtensionCommunicationService extends AbstractLocalStorageService implements AbstractApiRequestService {
  readonly unathrorizedError$ = new Subject<void>();

  private hasChromeRuntime: boolean = hasChromeRuntime();

  constructor(private ngZone?: NgZone) {
    super();
  }

  async getLocalStorage<T = string>(key: string, valueType: DataType = DataType.String): Promise<T> {
    return new Promise<T>(resolve => {
      if (this.hasChromeRuntime) {
        safeSendChromeRuntimeMessage({ action: BackgroundAction.GetLocalStorage, detail: { key } }, response => {
          resolve(this.getDataFromType(response.value, valueType) as T);
        });
      } else {
        const listener = (event: Event) => {
          const customEvent = event as CustomEvent;
          if (customEvent.type === AppAction.GetLocalStorageResponse && key === customEvent.detail.key) {
            window.removeEventListener(AppAction.GetLocalStorageResponse, listener);
            resolve(this.getDataFromType(customEvent.detail.value, valueType) as T);
          }
        };
        window.addEventListener(AppAction.GetLocalStorageResponse, listener);
        const event = new CustomEvent(ContentScriptAction.GetLocalStorage, {
          detail: { key },
        });
        window.dispatchEvent(event);
      }
    });
  }

  async setLocalStorage(key: string, value: unknown): Promise<void> {
    return new Promise<void>(resolve => {
      if (this.hasChromeRuntime) {
        safeSendChromeRuntimeMessage(
          { action: BackgroundAction.SetLocalStorage, detail: { key, value: this.getDataString(value) } },
          () => {
            resolve();
          }
        );
      } else {
        const listener = (event: Event) => {
          const customEvent = event as CustomEvent;
          if (customEvent.type === AppAction.SetLocalStorageResponse && customEvent.detail.key === key) {
            window.removeEventListener(AppAction.SetLocalStorageResponse, listener);
            resolve();
          }
        };
        window.addEventListener(AppAction.SetLocalStorageResponse, listener);
        const event = new CustomEvent(ContentScriptAction.SetLocalStorage, {
          detail: { key, value: this.getDataString(value) },
        });
        window.dispatchEvent(event);
      }
    });
  }

  handleLocalStorageChanges(): Observable<LocalStorageItem> {
    return new Observable<LocalStorageItem>(observer => {
      if (this.hasChromeRuntime) {
        chrome.runtime.onMessage.addListener((message, sender, sendResponse) => {
          if (message.action === AppAction.LocalStorageChanged) {
            this.pushObserverData(observer, message.detail.item);
          }
        });
      } else {
        const listener = (event: Event) => {
          const customEvent = event as CustomEvent;
          if (customEvent.type === AppAction.LocalStorageChanged) {
            this.pushObserverData(observer, customEvent.detail.item);
          }
        };
        window.addEventListener(AppAction.LocalStorageChanged, listener);
      }
    });
  }

  async getApiRequestData<T = unknown>(request: ApiRequest<unknown>, cacheResponse: boolean = true): Promise<T> {
    return this.sendApiRequest<T>(request, cacheResponse).then(response => response.data);
  }

  async getAppSyncQueryData<T = unknown>(
    query: string,
    variables?: { [key: string]: unknown },
    cacheResponse?: boolean
  ): Promise<T> {
    return this.sendAppSyncQuery<T>(query, variables, cacheResponse).then(response => response.data);
  }

  async getProfileEmail(): Promise<string> {
    return new Promise<string>(resolve => {
      if (this.hasChromeRuntime) {
        safeSendChromeRuntimeMessage(
          { action: BackgroundAction.GetProfileEmail },
          (response: { profileEmail: string }) => {
            resolve(response.profileEmail);
          }
        );
      } else {
        const listener = (event: Event) => {
          const customEvent = event as CustomEvent;
          if (customEvent.type === AppAction.GetProfileEmailResponse) {
            window.removeEventListener(AppAction.GetProfileEmailResponse, listener);
            resolve(customEvent.detail.profileEmail);
          }
        };
        window.addEventListener(AppAction.GetProfileEmailResponse, listener);
        window.dispatchEvent(new CustomEvent(ContentScriptAction.GetProfileEmail));
      }
    });
  }

  async getUserDetails(): Promise<UserDetails | null> {
    return new Promise<UserDetails | null>(resolve => {
      if (this.hasChromeRuntime) {
        safeSendChromeRuntimeMessage(
          { action: BackgroundAction.GetUserDetails },
          (response: { userDetails: UserDetails | null }) => {
            resolve(response.userDetails);
          }
        );
      } else {
        const listener = (event: Event) => {
          const customEvent = event as CustomEvent;
          if (customEvent.type === AppAction.GetUserDetailsResponse) {
            window.removeEventListener(AppAction.GetUserDetailsResponse, listener);
            resolve(customEvent.detail.userDetails);
          }
        };
        window.addEventListener(AppAction.GetUserDetailsResponse, listener);
        window.dispatchEvent(new CustomEvent(ContentScriptAction.GetUserDetails));
      }
    });
  }

  async loadRecommendations(): Promise<void> {
    await safeSendChromeRuntimeMessageAsync({ action: BackgroundAction.LoadRecommendations });
  }

  async sendApiRequest<T = unknown, E = unknown>(
    apiRequest: ApiRequest,
    cacheResponse?: boolean
  ): Promise<ApiResponse<T, E>> {
    // TBD
    captureSentryMessage('Sending API request', { extra: { apiRequest } });
    if (typeof cacheResponse !== 'boolean') {
      cacheResponse = true;
    }
    return new Promise<ApiResponse<T, E>>((resolve, reject) => {
      safeSendChromeRuntimeMessage(
        { action: BackgroundAction.ApiRequest, detail: { apiRequest, cacheResponse } },
        (response: ApiResponse<T, E>) => {
          safeSendChromeRuntimeMessage({ action: BackgroundAction.ClearApiRequestCache, detail: apiRequest });
          if (((response.error as ApolloError)?.networkError as ServerError)?.statusCode === 401) {
            this.unathrorizedError$.next();
          }
          if (response.success) {
            resolve(response);
          } else {
            reject(response.error);
          }
        }
      );
    });
  }

  async sendAppSyncQuery<T = unknown, E = unknown>(
    query: string,
    variables?: { [key: string]: unknown },
    cacheResponse?: boolean
  ): Promise<ApiResponse<T, E>> {
    return this.sendApiRequest<T, E>(
      { type: ApiRequestType.AppSyncQuery, data: { queryOrMutation: query, variables } as GraphQLRequestData },
      cacheResponse
    );
  }

  subscribe<T = unknown>(query: string, variables?: { [key: string]: unknown }): Observable<T> {
    throw new Error('Not implemented');
  }

  async navigateToUrl(url: string): Promise<void> {
    safeSendChromeRuntimeMessage({ action: BackgroundAction.NavigateToUrl, detail: { url } });
  }

  async updateIsExtensionPopupOpened(isOpened: boolean): Promise<void> {
    safeSendChromeRuntimeMessage({ action: BackgroundAction.UpdateIsExtensionPopupOpened, detail: { isOpened } });
  }

  async updateUserDetails(userDetails: UserDetails | null): Promise<void> {
    safeSendChromeRuntimeMessage({ action: BackgroundAction.UpdateUserDetails, detail: { userDetails } });
  }

  async sendExtensionAuthStatusChanged(email?: string): Promise<void> {
    safeSendChromeRuntimeMessage({ action: BackgroundAction.ExtensionAuthStatusChanged, detail: { email } });
  }

  async trackAnalyticsEvent(event: AnalyticsEvent, properties?: Dict): Promise<void> {
    safeSendChromeRuntimeMessage({ action: BackgroundAction.TrackAnalyticsEvent, detail: { event, properties } });
  }

  async signIn(username: string, password: string): Promise<void> {
    safeSendChromeRuntimeMessage({ action: BackgroundAction.SignIn, detail: { username, password } });
  }

  async signOut(): Promise<void> {
    safeSendChromeRuntimeMessage({ action: BackgroundAction.SignOut });
  }

  sendViewChanged(currentView: AppView, showOverlay?: boolean): void {
    const event = new CustomEvent(ContentScriptAction.ViewChanged, {
      detail: { currentView, showOverlay },
    });
    window.dispatchEvent(event);
  }

  async isServiceWorkerActive(): Promise<boolean> {
    captureSentryMessage('Checking if service worker is active');
    return new Promise(resolve => {
      const timeoutId: NodeJS.Timeout = setTimeout(() => {
        captureSentryMessage('Service worker check timed out');
        resolve(false);
      }, CHECK_SERVICE_WORKER_STATE_TIMEOUT_MS);
      safeSendChromeRuntimeMessage({ action: BackgroundAction.Ping }, (response: { isActive: boolean }) => {
        clearTimeout(timeoutId);
        captureSentryMessage('Service worker response received', { extra: { response } });
        resolve(!!response && response.isActive);
      });
    });
  }

  private pushObserverData<T = unknown>(observer: Observer<T>, data: T): void {
    if (this.ngZone) {
      this.ngZone.run(() => {
        observer.next(data);
      });
    } else {
      observer.next(data);
    }
  }
}
